import React from 'react';
import { clsx } from 'clsx';
import { designTokens } from '../../styles/design-tokens';

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'highlighted' | 'interactive' | 'subtle';
  padding?: 'none' | 'sm' | 'md' | 'lg';
  shadow?: 'none' | 'sm' | 'md' | 'lg';
  rounded?: 'none' | 'sm' | 'md' | 'lg' | 'xl';
  children: React.ReactNode;
  onClick?: () => void;
  isLoading?: boolean;
  animate?: boolean;
}

export const Card = React.forwardRef<HTMLDivElement, CardProps>(({
  variant = 'default',
  padding = 'md',
  shadow = 'sm',
  rounded = 'lg',
  children,
  className,
  onClick,
  isLoading = false,
  animate = true,
  ...props
}, ref) => {
  const baseClasses = 'bg-white transition-all duration-200';
  
  const variantClasses = {
    default: 'border border-gray-200',
    highlighted: 'border-2 border-[#4B5FDC] shadow-md',
    interactive: 'border border-gray-200 hover:shadow-md hover:border-gray-300 cursor-pointer transform hover:scale-[1.01]',
    subtle: 'border border-gray-100 bg-gray-50',
  };
  
  const paddingClasses = {
    none: '',
    sm: 'p-4',    // 16px
    md: 'p-6',    // 24px
    lg: 'p-8',    // 32px
  };
  
  const shadowClasses = {
    none: '',
    sm: 'shadow-sm',
    md: 'shadow-md',
    lg: 'shadow-lg',
  };
  
  const roundedClasses = {
    none: '',
    sm: 'rounded',      // 4px
    md: 'rounded-lg',   // 8px
    lg: 'rounded-xl',   // 12px
    xl: 'rounded-2xl',  // 16px
  };

  const handleClick = () => {
    if (onClick && !isLoading) {
      onClick();
    }
  };

  return (
    <div
      ref={ref}
      className={clsx(
        baseClasses,
        variantClasses[variant],
        paddingClasses[padding],
        shadowClasses[shadow],
        roundedClasses[rounded],
        animate && 'animate-fadeIn',
        isLoading && 'pointer-events-none opacity-60',
        className
      )}
      onClick={handleClick}
      {...props}
    >
      {isLoading ? (
        <div className="space-y-3">
          <div className="skeleton h-4 w-3/4 rounded" />
          <div className="skeleton h-4 w-1/2 rounded" />
          <div className="skeleton h-4 w-5/6 rounded" />
        </div>
      ) : (
        children
      )}
    </div>
  );
});

Card.displayName = 'Card';

// Card sub-components for better composition
export interface CardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  title?: string;
  subtitle?: string;
  action?: React.ReactNode;
}

export const CardHeader: React.FC<CardHeaderProps> = ({
  title,
  subtitle,
  action,
  children,
  className,
  ...props
}) => {
  return (
    <div
      className={clsx(
        'flex items-start justify-between pb-4 border-b border-gray-200',
        className
      )}
      {...props}
    >
      {children || (
        <>
          <div>
            {title && (
              <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
            )}
            {subtitle && (
              <p className="mt-1 text-sm text-gray-500">{subtitle}</p>
            )}
          </div>
          {action && <div>{action}</div>}
        </>
      )}
    </div>
  );
};

export interface CardContentProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

export const CardContent: React.FC<CardContentProps> = ({
  children,
  className,
  ...props
}) => {
  return (
    <div className={clsx('py-4', className)} {...props}>
      {children}
    </div>
  );
};

export interface CardFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  align?: 'left' | 'center' | 'right' | 'between';
}

export const CardFooter: React.FC<CardFooterProps> = ({
  children,
  align = 'right',
  className,
  ...props
}) => {
  const alignClasses = {
    left: 'justify-start',
    center: 'justify-center',
    right: 'justify-end',
    between: 'justify-between',
  };

  return (
    <div
      className={clsx(
        'flex items-center pt-4 border-t border-gray-200',
        alignClasses[align],
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
};

// Specialized card variants
export interface MetricCardProps {
  label: string;
  value: string | number;
  trend?: 'up' | 'down' | 'neutral';
  trendValue?: string;
  icon?: React.ReactNode;
}

export const MetricCard: React.FC<MetricCardProps> = ({
  label,
  value,
  trend,
  trendValue,
  icon,
}) => {
  const trendColors = {
    up: 'text-green-600',
    down: 'text-red-600',
    neutral: 'text-gray-500',
  };

  const trendIcons = {
    up: '↗️',
    down: '↘️',
    neutral: '→',
  };

  return (
    <Card variant="default" padding="sm" className="hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-600">{label}</p>
          <p className="mt-2 text-3xl font-semibold text-gray-900">{value}</p>
          {trend && (
            <p className={clsx('mt-2 text-sm', trendColors[trend])}>
              <span className="mr-1">{trendIcons[trend]}</span>
              {trendValue}
            </p>
          )}
        </div>
        {icon && (
          <div className="ml-4 flex-shrink-0 text-gray-400">{icon}</div>
        )}
      </div>
    </Card>
  );
};